import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Alert, Badge } from 'react-bootstrap';
import { projectsApi } from '../services/api';
import { Project } from '../types';

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const data = await projectsApi.getAll();
      console.log('📋 Projetos carregados na listagem:', data);
      console.log('📋 Total de projetos:', data.length);
      if (data.length > 0) {
        console.log('📋 Primeiro projeto:', data[0]);
      }
      setProjects(data);
    } catch (error: any) {
      console.error('❌ Error loading projects:', error);

      if (error.message?.includes('401') || error.message?.includes('expirada')) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      setError('Erro ao carregar projetos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await projectsApi.delete(id);
        setSuccess('Projeto excluído com sucesso!');
        fetchProjects();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        console.error('Error deleting project:', err);
        setError(err.message || 'Erro ao excluir projeto');
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      // Remove o timezone para evitar problemas de fuso horário
      const date = new Date(dateString.replace('Z', ''));

      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        console.warn('Data inválida:', dateString);
        return '-';
      }

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-';
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'Em Andamento': 'primary',
      'Concluído': 'success',
      'Planejado': 'warning',
    };
    return statusMap[status] || 'secondary';
  };

  const getImagemPrincipal = (project: Project): string | null => {
    // Verifica se tem galeria e fotos
    if (!project.galeriaFotos?.fotos?.length) {
      return null;
    }

    // Tenta pegar a foto marcada como principal
    const fotoPrincipal = project.galeriaFotos.fotos.find(f => f.isPrincipal);
    if (fotoPrincipal) {
      return fotoPrincipal.url;
    }

    // Se não tem principal, retorna a primeira foto
    return project.galeriaFotos.fotos[0].url;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="bi bi-folder me-2"></i>Gerenciar Projetos</h2>
        <Button variant="primary" onClick={() => navigate('/admin/projetos/criar')}>
          <i className="bi bi-plus-lg me-2"></i>
          Novo Projeto
        </Button>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
          <Table responsive hover>
            <thead style={{ background: 'var(--color-primary)', color: 'white' }}>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th style={{ width: '80px' }}>Imagem</th>
                <th>Título</th>
                <th style={{ width: '130px' }}>Categoria</th>
                <th style={{ width: '150px' }}>Local</th>
                <th style={{ width: '120px' }}>Data</th>
                <th style={{ width: '120px' }}>Status</th>
                <th style={{ width: '220px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-5 text-muted">
                    Nenhum projeto cadastrado. Clique em "Novo Projeto" para começar.
                  </td>
                </tr>
              ) : (
                projects.map((project) => {
                  const imagemUrl = getImagemPrincipal(project);
                  return (
                  <tr key={project.id}>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>{project.id}</td>
                    <td>
                      {imagemUrl ? (
                        <img
                          src={imagemUrl}
                          alt={project.titulo}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '60px',
                            height: '60px',
                            background: 'var(--color-gray-light)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <i className="bi bi-image text-muted"></i>
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{project.titulo}</strong>
                    </td>
                    <td>
                      {project.categoriaLabel
                        ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{project.categoriaLabel}</Badge>
                        : <span className="text-muted">-</span>}
                    </td>
                    <td>{project.local || '-'}</td>
                    <td>{formatDate(project.dataProjeto)}</td>
                    <td>
                      <Badge bg={getStatusBadge(project.status)}>{project.status}</Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/admin/projetos/editar/${project.id}`)}
                        title="Editar"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(project.id)}
                        title="Excluir"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
